import { useCallback, useEffect, useState, type FormEvent } from 'react';

import {
  ApiError,
  completeOperatorSetup,
  createCompany,
  currentVisit,
  describeCompany,
  endSessionsFor,
  enterCompany,
  issueResetFor,
  leaveCompany,
  listCompanies,
  listOperatorEvents,
  operatorMe,
  operatorSignIn,
  operatorSignOut,
  setCompanySuspended,
  unlockMember,
  visitsFor,
  type CompanyDetail,
  type CompanySummary,
  type OperatorEvent,
  type OperatorMe,
  type PastVisit,
  type SupportVisitState,
} from '../api.js';
import { formatDate, roleLabel, type Role } from '../labels.js';

/**
 * The operator console — the vendor's own screen, not part of the product.
 *
 * IT LOOKS DELIBERATELY UNLIKE THE APP. Different door, different cookie,
 * different job. Somebody with both open should never be in doubt about which
 * window they are typing into, because one of them belongs to a customer.
 *
 * WHAT IT DOES NOT SHOW
 * ---------------------
 * Any customer's actual work. There are no projects here, no snags, no costs —
 * only whether a company is alive and whether its people can get in. That is
 * the shape of nearly every support call, and keeping the console to it is what
 * stops "I checked they had users" turning into an afternoon reading a
 * contractor's margins.
 *
 * ONE-TIME LINKS ARE SHOWN ONCE, AND THE SCREEN SAYS SO
 * ----------------------------------------------------
 * Only the hash of an invitation or reset token is stored, so a link genuinely
 * cannot be shown again. Somebody who closes the panel expecting to find it
 * later would be stuck, so the screen has to be honest about it at the moment
 * it appears.
 */

type State =
  | { readonly status: 'checking' }
  | { readonly status: 'signed_out' }
  | { readonly status: 'signed_in'; readonly me: OperatorMe };

interface Props {
  /** Present when arriving on `/operator/setup/<token>`. */
  readonly setupToken?: string | null;
}

export function Operator({ setupToken = null }: Props): JSX.Element {
  const [state, setState] = useState<State>({ status: 'checking' });

  const refresh = useCallback(async () => {
    try {
      setState({ status: 'signed_in', me: await operatorMe() });
    } catch {
      setState({ status: 'signed_out' });
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  if (state.status === 'checking') {
    return (
      <main className="auth-shell">
        <p aria-live="polite">Loading…</p>
      </main>
    );
  }

  /*
   * THE SESSION IS CHECKED BEFORE THE TOKEN, AND THAT ORDER IS THE FIX.
   *
   * A setup link is single-use, so the second visit is refused — correctly. But
   * the person making that second visit is, almost always, the person who just
   * used it: they have a live session, and what they wanted was the console.
   * Showing them "that setup link is not valid" in red is the app being
   * technically right and practically useless, and it happened on the very
   * first real use of this screen.
   *
   * So: already signed in wins over any token in the URL. A used link now just
   * takes you where you were going.
   */
  if (state.status === 'signed_in') {
    return <Console me={state.me} onSignedOut={() => setState({ status: 'signed_out' })} />;
  }

  if (setupToken) return <SetupForm token={setupToken} onDone={refresh} />;

  return <SignInForm onSignedIn={refresh} />;
}

// ---------------------------------------------------------------------------

function SetupForm({ token, onDone }: { token: string; onDone: () => void }): JSX.Element {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [again, setAgain] = useState('');
  const [error, setError] = useState<string | null>(null);
  /** A used link is not an error to argue with — it is a signpost. */
  const [spent, setSpent] = useState(false);
  const [busy, setBusy] = useState(false);

  const submit = async (event: FormEvent): Promise<void> => {
    event.preventDefault();
    if (password !== again) {
      setError('Those two passwords are not the same.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await completeOperatorSetup(token, email, password);
      // Signed in with the password just chosen, so the console opens straight
      // away rather than asking for it again.
      await operatorSignIn(email, password);
      onDone();
    } catch (caught) {
      /*
       * The server answers the same way whether the link was used, expired, or
       * never existed — deliberately, so a stranger cannot learn which. But the
       * person in front of us is overwhelmingly the one who already used it, so
       * the screen offers the next step instead of a dead end.
       */
      if (caught instanceof ApiError && caught.status === 400) setSpent(true);
      else setError(caught instanceof ApiError ? caught.message : 'That did not work.');
    } finally {
      setBusy(false);
    }
  };

  if (spent) {
    return (
      <main className="auth-shell operator-shell">
        <div className="card">
          <h1 className="brand">This link has already been used</h1>
          <p className="brand-sub">
            A setup link works once. If you have already chosen a password, sign in with it — your
            account is ready.
          </p>
          <button type="button" onClick={() => window.location.assign('/operator')}>
            Go to the sign-in
          </button>
          <p className="hint">
            If you never got as far as choosing a password, the link may have expired instead. Issue
            yourself a new one with <code>scripts/create-operator.mjs</code>.
          </p>
        </div>
      </main>
    );
  }

  return (
    <main className="auth-shell operator-shell">
      {/*
        `.field` around every label and input, exactly as `Login.tsx` does.
        Without it a label and its input are two inline siblings with no layout
        of their own: they sit side by side, overlap, and the card reads as a
        jumble. The class carries the block display, the spacing and the 44px
        minimum target size — none of which is worth a second copy here.
      */}
      <form className="card" onSubmit={(e) => void submit(e)}>
        <h1 className="brand">Set up your operator account</h1>
        <p className="brand-sub">
          Not a company account. It exists to support the companies you sell to.
        </p>

        {error && (
          <div className="alert" role="alert">
            <span>{error}</span>
          </div>
        )}

        <div className="field">
          <label htmlFor="op-email">Your email address</label>
          <input
            id="op-email"
            type="email"
            autoComplete="username"
            autoCapitalize="none"
            spellCheck={false}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={busy}
            required
          />
        </div>

        <div className="field">
          <label htmlFor="op-pw">Choose a password</label>
          <input
            id="op-pw"
            type="password"
            autoComplete="new-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            disabled={busy}
            required
          />
        </div>

        <div className="field">
          <label htmlFor="op-pw2">And again</label>
          <input
            id="op-pw2"
            type="password"
            autoComplete="new-password"
            value={again}
            onChange={(e) => setAgain(e.target.value)}
            disabled={busy}
            required
          />
        </div>

        <button type="submit" disabled={busy || !email || !password || !again}>
          {busy ? 'Setting up…' : 'Set up and sign in'}
        </button>
      </form>
    </main>
  );
}

function SignInForm({ onSignedIn }: { onSignedIn: () => void }): JSX.Element {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async (event: FormEvent): Promise<void> => {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await operatorSignIn(email, password);
      onSignedIn();
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'That did not work.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <main className="auth-shell operator-shell">
      <form className="card" onSubmit={(e) => void submit(e)}>
        <h1 className="brand">Operator console</h1>
        <p className="brand-sub">Not a company account. This is the support console.</p>

        {error && (
          <div className="alert" role="alert">
            <span>{error}</span>
          </div>
        )}

        <div className="field">
          <label htmlFor="op-in-email">Email address</label>
          <input
            id="op-in-email"
            type="email"
            autoComplete="username"
            autoCapitalize="none"
            spellCheck={false}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={busy}
            required
          />
        </div>

        <div className="field">
          <label htmlFor="op-in-pw">Password</label>
          <input
            id="op-in-pw"
            type="password"
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            disabled={busy}
            required
          />
        </div>

        <button type="submit" disabled={busy || !email || !password}>
          {busy ? 'Signing in…' : 'Sign in'}
        </button>
      </form>
    </main>
  );
}

// ---------------------------------------------------------------------------

function Console({ me, onSignedOut }: { me: OperatorMe; onSignedOut: () => void }): JSX.Element {
  const [companies, setCompanies] = useState<readonly CompanySummary[] | null>(null);
  const [chosen, setChosen] = useState<CompanyDetail | null>(null);
  const [events, setEvents] = useState<readonly OperatorEvent[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [adding, setAdding] = useState(false);
  /** A link that will never be shown again. Held until dismissed. */
  const [link, setLink] = useState<{ what: string; path: string; note: string } | null>(null);
  /**
   * A visit left open — usually a tab closed without leaving.
   *
   * Checked on load rather than assumed absent, because the visit outlives this
   * screen: it lives in the database with a clock, not in this component. An
   * operator who closed the window and came back must be told they are still
   * inside somebody's company, and given the one button that ends it.
   */
  const [visit, setVisit] = useState<SupportVisitState | null>(null);

  const load = useCallback(async () => {
    try {
      setCompanies(await listCompanies());
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'Could not load the companies.');
    }
    try {
      setVisit(await currentVisit());
    } catch {
      /* the console still works without knowing; the banner is the reminder */
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const open = async (tenantId: string): Promise<void> => {
    setError(null);
    try {
      setChosen(await describeCompany(tenantId));
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'Could not open that company.');
    }
  };

  const act = async (what: string, fn: () => Promise<void>): Promise<void> => {
    setBusy(true);
    setError(null);
    try {
      await fn();
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : `${what} did not work.`);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="operator-console">
      <header className="operator-bar">
        <div>
          <strong>Operator console</strong>
          <span className="hint"> — {me.name}</span>
        </div>
        <button
          type="button"
          className="link-button"
          onClick={() =>
            void act('Signing out', async () => {
              await operatorSignOut();
              onSignedOut();
            })
          }
        >
          Sign out
        </button>
      </header>

      <main className="operator-main">
        {error && (
          <div className="alert" role="alert">
            <span>{error}</span>
          </div>
        )}

        {/*
          STILL INSIDE SOMEBODY'S COMPANY.

          The likeliest way this feature goes wrong is not a bad decision, it is
          a forgotten tab — so the console says so the moment it loads, gives the
          way back in, and gives the way out. The clock ends it regardless after
          thirty minutes; this is what makes that visible rather than merely
          true.
        */}
        {visit && (
          <div className="operator-visit" role="status">
            <h3>You are inside {visit.company}</h3>
            <p className="hint">
              Read only, until {formatDate(visit.expires_at)}
              {visit.money_visible ? ' · their money is visible' : ' · their money is hidden'}. They
              can see that you are there.
            </p>
            <div className="button-row">
              <button
                type="button"
                className="inline"
                onClick={() => window.location.assign('/')}
              >
                Go to their app
              </button>
              <button
                type="button"
                className="secondary inline"
                disabled={busy}
                onClick={() =>
                  void act('Leaving', async () => {
                    await leaveCompany();
                    setVisit(null);
                  })
                }
              >
                Leave now
              </button>
            </div>
          </div>
        )}

        {link && (
          <div className="operator-link" role="status">
            <h3>{link.what}</h3>
            <p className="hint">{link.note}</p>
            <code className="operator-link-value">{window.location.origin + link.path}</code>
            <div className="button-row">
              <button
                type="button"
                className="inline"
                onClick={() => void navigator.clipboard?.writeText(window.location.origin + link.path)}
              >
                Copy
              </button>
              <button type="button" className="secondary inline" onClick={() => setLink(null)}>
                I have sent it
              </button>
            </div>
          </div>
        )}

        <div className="section-head">
          <h2>Companies</h2>
          <button type="button" className="inline" onClick={() => setAdding((was) => !was)}>
            {adding ? 'Cancel' : 'Add a company'}
          </button>
        </div>

        {adding && (
          <NewCompany
            busy={busy}
            onCreate={(input) =>
              act('Creating the company', async () => {
                const made = await createCompany(input);
                setAdding(false);
                setLink({
                  what: `${made.name} is ready`,
                  path: made.invitation_path,
                  note:
                    'Send this to their founder. It works once, expires in 72 hours, and CANNOT be shown ' +
                    'again — only its fingerprint is stored. If it is lost, issue another from their company.',
                });
                await load();
              })
            }
          />
        )}

        {companies === null && !error && <p aria-live="polite">Loading…</p>}

        {companies && companies.length === 0 && (
          <div className="empty">
            <p>No companies yet. Add the first one.</p>
          </div>
        )}

        {companies && companies.length > 0 && (
          <div className="table-scroll">
            <table className="grid">
              <thead>
                <tr>
                  <th scope="col">Company</th>
                  <th scope="col">People</th>
                  <th scope="col">Projects</th>
                  <th scope="col">Last used</th>
                  <th scope="col">State</th>
                  <th scope="col"> </th>
                </tr>
              </thead>
              <tbody>
                {companies.map((c) => (
                  <tr key={c.tenant_id}>
                    <td>
                      <strong>{c.name}</strong>
                      {c.legal_name && <div className="hint">{c.legal_name}</div>}
                    </td>
                    <td>{c.people}</td>
                    <td>{c.projects}</td>
                    <td>
                      {c.last_activity_at ? (
                        formatDate(c.last_activity_at)
                      ) : (
                        <span className="hint">never</span>
                      )}
                    </td>
                    <td>
                      {c.suspended_at ? (
                        <span className="pill pill-blocked">Suspended</span>
                      ) : c.never_used ? (
                        // The most useful thing on this screen: a company that
                        // was created and never opened is a customer who never
                        // got in, and nobody finds that out on their own.
                        <span className="pill pill-risk">Nobody has signed in</span>
                      ) : (
                        <span className="pill pill-ok">In use</span>
                      )}
                      {c.pending_invitations > 0 && (
                        <div className="hint">{c.pending_invitations} invitation(s) waiting</div>
                      )}
                    </td>
                    <td>
                      <button type="button" className="link-button" onClick={() => void open(c.tenant_id)}>
                        Open
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {chosen && (
          <CompanyPanel
            company={chosen}
            busy={busy}
            onClose={() => setChosen(null)}
            onReset={(userId, name) =>
              act('Issuing the link', async () => {
                const issued = await issueResetFor(chosen.tenant_id, userId);
                setLink({
                  what: `Password reset for ${name}`,
                  path: issued.path,
                  note:
                    'Send it to them directly. It works once and cannot be shown again. ' +
                    'It will appear in their company’s own audit trail as issued by you.',
                });
              })
            }
            onUnlock={(userId) =>
              act('Unlocking', async () => {
                await unlockMember(chosen.tenant_id, userId);
                setChosen(await describeCompany(chosen.tenant_id));
              })
            }
            onEndSessions={(userId) =>
              act('Ending the sessions', async () => {
                await endSessionsFor(chosen.tenant_id, userId);
                setChosen(await describeCompany(chosen.tenant_id));
              })
            }
            onSuspend={(suspended, reason) =>
              act('Changing the suspension', async () => {
                await setCompanySuspended(chosen.tenant_id, suspended, reason);
                setChosen(await describeCompany(chosen.tenant_id));
                await load();
              })
            }
            /*
              Entering takes you STRAIGHT into their app, in this same tab.
              There is no confirmation step and no interstitial: the reason was
              already typed, the customer has already been told, and a screen
              that says "are you sure" after all that is one more click for
              nothing. The banner at the top of their app is the thing that
              makes the state obvious, and it is unmissable.
            */
            onEnter={(input) =>
              act('Entering the company', async () => {
                await enterCompany(chosen.tenant_id, input);
                window.location.assign('/');
              })
            }
          />
        )}

        <div className="section-head">
          <h2>What you have done</h2>
          <button
            type="button"
            className="link-button"
            onClick={() => void act('Loading the trail', async () => setEvents(await listOperatorEvents()))}
          >
            {events ? 'Refresh' : 'Show'}
          </button>
        </div>

        {events && (
          <div className="table-scroll">
            <table className="grid">
              <thead>
                <tr>
                  <th scope="col">When</th>
                  <th scope="col">Who</th>
                  <th scope="col">What</th>
                  <th scope="col">Company</th>
                </tr>
              </thead>
              <tbody>
                {events.map((e, i) => (
                  <tr key={`${e.occurred_at}-${i}`}>
                    <td>{formatDate(e.occurred_at)}</td>
                    <td>{e.operator_name ?? '—'}</td>
                    <td>{e.action.replace(/^operator\./, '').replace(/_/g, ' ')}</td>
                    <td>{e.tenant_name ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </main>
    </div>
  );
}

// ---------------------------------------------------------------------------

function NewCompany({
  busy,
  onCreate,
}: {
  readonly busy: boolean;
  readonly onCreate: (input: { name: string; admin_email: string; legal_name?: string }) => void;
}): JSX.Element {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [legal, setLegal] = useState('');

  return (
    <form
      className="operator-form"
      onSubmit={(e) => {
        e.preventDefault();
        onCreate({ name, admin_email: email, ...(legal ? { legal_name: legal } : {}) });
      }}
    >
      <div className="field">
        <label htmlFor="new-name">Company name</label>
        <input id="new-name" value={name} onChange={(e) => setName(e.target.value)} required />
      </div>
      <div className="field">
        <label htmlFor="new-legal">Legal name (optional)</label>
        <input id="new-legal" value={legal} onChange={(e) => setLegal(e.target.value)} />
      </div>
      <div className="field">
        <label htmlFor="new-admin">Their admin’s email</label>
        <input
          id="new-admin"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
        />
        <p className="hint">
          They receive a one-time link and choose their own password. You never see it and cannot
          sign in as them.
        </p>
      </div>
      <button type="submit" className="inline" disabled={busy}>
        {busy ? 'Creating…' : 'Create the company'}
      </button>
    </form>
  );
}

function CompanyPanel({
  company,
  busy,
  onClose,
  onReset,
  onUnlock,
  onEndSessions,
  onSuspend,
  onEnter,
}: {
  readonly company: CompanyDetail;
  readonly busy: boolean;
  readonly onClose: () => void;
  readonly onReset: (userId: string, name: string) => void;
  readonly onUnlock: (userId: string) => void;
  readonly onEndSessions: (userId: string) => void;
  readonly onSuspend: (suspended: boolean, reason?: string) => void;
  readonly onEnter: (input: {
    reason: string;
    include_money?: boolean;
    money_reason?: string;
  }) => void;
}): JSX.Element {
  const [reason, setReason] = useState('');

  return (
    <section className="operator-panel">
      <div className="section-head">
        <h2>{company.name}</h2>
        <button type="button" className="link-button" onClick={onClose}>
          Close
        </button>
      </div>

      <p className="hint">
        {company.people} people · {company.projects} projects · created {formatDate(company.created_at)}
        {company.last_activity_at ? ` · last used ${formatDate(company.last_activity_at)}` : ' · never used'}
      </p>

      {/* Said plainly, because it is the thing that decides what to do next. */}
      <p className="hint">
        You are looking at this company’s ACCOUNT, not its work. Their projects, snags and costs are
        not visible here — entering the company below is the only way to see those, and they are
        told when you do.
      </p>

      {company.members.length === 0 ? (
        <div className="empty">
          <p>Nobody has joined yet — their founder has not redeemed their invitation.</p>
        </div>
      ) : (
        <div className="table-scroll">
          <table className="grid">
            <thead>
              <tr>
                <th scope="col">Name</th>
                <th scope="col">Role</th>
                <th scope="col">Last signed in</th>
                <th scope="col">Now</th>
                <th scope="col">Help them</th>
              </tr>
            </thead>
            <tbody>
              {company.members.map((m) => (
                <tr key={m.user_id}>
                  <td>
                    <strong>{m.name}</strong>
                    <div className="hint">{m.email}</div>
                  </td>
                  <td>{roleLabel(m.role as Role)}</td>
                  <td>
                    {m.last_login_at ? formatDate(m.last_login_at) : <span className="hint">never</span>}
                    {m.locked && <div className="pill pill-blocked">Locked out</div>}
                  </td>
                  <td>{m.live_sessions > 0 ? `${m.live_sessions} signed in` : '—'}</td>
                  <td>
                    <div className="button-row">
                      <button
                        type="button"
                        className="link-button"
                        disabled={busy}
                        onClick={() => onReset(m.user_id, m.name)}
                      >
                        Reset password
                      </button>
                      {m.locked && (
                        <button
                          type="button"
                          className="link-button"
                          disabled={busy}
                          onClick={() => onUnlock(m.user_id)}
                        >
                          Unlock
                        </button>
                      )}
                      {m.live_sessions > 0 && (
                        <button
                          type="button"
                          className="link-button"
                          disabled={busy}
                          onClick={() => onEndSessions(m.user_id)}
                        >
                          Sign them out
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <EnterCompany company={company} busy={busy} onEnter={onEnter} />

      <h3 className="sub-head">Suspension</h3>
      {company.suspended_at ? (
        <>
          <p className="hint">
            Suspended {formatDate(company.suspended_at)} — {company.suspended_reason}
          </p>
          <button type="button" className="inline" disabled={busy} onClick={() => onSuspend(false)}>
            Let them back in
          </button>
        </>
      ) : (
        <>
          <p className="hint">
            Suspending signs everybody out at once and keeps them out. They will see the reason in
            their own audit trail when they return.
          </p>
          <div className="field">
            <label htmlFor="susp-reason">Why</label>
            <input
              id="susp-reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Non-payment: three invoices outstanding since June"
            />
          </div>
          <button
            type="button"
            className="secondary inline"
            disabled={busy || reason.trim().length < 10}
            onClick={() => onSuspend(true, reason)}
          >
            Suspend this company
          </button>
        </>
      )}
    </section>
  );
}

/**
 * Entering a company — thirty minutes of read-only eyes on their work.
 *
 * THE FORM IS THE CONTROL. There is no confirmation dialog after it, because
 * everything a dialog would ask has already been asked here: why you are going
 * in, and whether you need their money. What makes this safe is not a second
 * click — it is that the reason is written into THEIR audit trail unedited, and
 * that a banner appears on their screens while you are there.
 *
 * The money tick is deliberately awkward. It reveals a second box that must
 * also be filled in, and the sentence beside it says what the customer will be
 * told. That friction is the point: the user chose "hidden unless you tick a
 * box" over "always visible", and a tick with no cost attached would drift into
 * being ticked every time.
 */
function EnterCompany({
  company,
  busy,
  onEnter,
}: {
  readonly company: CompanyDetail;
  readonly busy: boolean;
  readonly onEnter: (input: {
    reason: string;
    include_money?: boolean;
    money_reason?: string;
  }) => void;
}): JSX.Element {
  const [why, setWhy] = useState('');
  const [money, setMoney] = useState(false);
  const [moneyWhy, setMoneyWhy] = useState('');
  const [past, setPast] = useState<readonly PastVisit[] | null>(null);

  const ready = why.trim().length >= 10 && (!money || moneyWhy.trim().length >= 10);

  return (
    <>
      <h3 className="sub-head">Enter this company</h3>

      <p className="hint">
        You will see their projects, drawings, snags and documents as they are — and you will not be
        able to change one thing, download a file, or export anything. It lasts 30 minutes and then
        ends on its own. <strong>{company.name} sees a banner while you are in</strong>, and both
        the reason and the visit go into their own audit trail.
      </p>

      <div className="field">
        <label htmlFor="visit-why">Why you are going in — they will read this</label>
        <input
          id="visit-why"
          value={why}
          onChange={(e) => setWhy(e.target.value)}
          placeholder="Checking why the snag list is blank for Sara"
        />
      </div>

      <div className="field">
        {/* A checkbox inside its own label, as every other tick in the app is. */}
        <label htmlFor="visit-money">
          <input
            id="visit-money"
            type="checkbox"
            checked={money}
            onChange={(e) => setMoney(e.target.checked)}
          />{' '}
          I also need to see their money
        </label>
        <p className="hint">
          Off, their costs, budgets, variations and invoices are invisible to you — not hidden on
          the screen, not sent at all. Leave it off unless the problem is on one of those screens.
        </p>
      </div>

      {money && (
        <div className="field">
          <label htmlFor="visit-money-why">Why you need their money — they read this too</label>
          <input
            id="visit-money-why"
            value={moneyWhy}
            onChange={(e) => setMoneyWhy(e.target.value)}
            placeholder="The variation total on VO-014 is wrong on their screen"
          />
        </div>
      )}

      <div className="button-row">
        <button
          type="button"
          className="inline"
          disabled={busy || !ready}
          onClick={() =>
            onEnter({
              reason: why.trim(),
              ...(money ? { include_money: true, money_reason: moneyWhy.trim() } : {}),
            })
          }
        >
          {busy ? 'Entering…' : `Enter ${company.name}`}
        </button>
        <button
          type="button"
          className="link-button"
          onClick={() => void visitsFor(company.tenant_id).then(setPast)}
        >
          {past ? 'Refresh the visits' : 'Who has been in before'}
        </button>
      </div>

      {past && past.length === 0 && <p className="hint">Nobody has ever entered this company.</p>}

      {past && past.length > 0 && (
        <div className="table-scroll">
          <table className="grid">
            <thead>
              <tr>
                <th scope="col">When</th>
                <th scope="col">Who</th>
                <th scope="col">Why</th>
                <th scope="col">Money</th>
                <th scope="col">Ended</th>
              </tr>
            </thead>
            <tbody>
              {past.map((v) => (
                <tr key={`${v.started_at}-${v.operator_name ?? ''}`}>
                  <td>{formatDate(v.started_at)}</td>
                  <td>{v.operator_name ?? '—'}</td>
                  <td>{v.reason}</td>
                  <td>{v.include_money ? 'seen' : 'hidden'}</td>
                  <td>
                    {v.ended_at ? (
                      `${formatDate(v.ended_at)} (${v.ended_reason ?? 'ended'})`
                    ) : (
                      <span className="pill pill-risk">open</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}
